import { Button, Modal } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BarcodeConflictParams, BarcodeCreate } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { toastError, toastSuccess } from '../../../lib/notify';
import { createBarcode, teachBarcode } from '../api';
import { BarcodeConflictNotice, barcodeConflictOf } from '../components/BarcodeConflict';
import { SelectField, TextField } from '../components/form';
import { useSuppliers, useUnits } from '../lookups';

interface BarcodeFormValues {
  articleId: string;
  barcode: string;
  kind: '' | 'gtin' | 'raw';
  supplierId: string;
  unitId: string;
  qtyPerScan: string;
  note: string;
}

/**
 * Manual barcode registration (Victor Q16) and the teach flow — one form,
 * because they write the same row through the same server-side path and
 * differ only in who is allowed and whether `taught_by` is stamped.
 *
 * A collision does not close the dialog: the conflict notice replaces the
 * error toast so the operator can see WHICH article owns the code and decide,
 * instead of retrying the same scan.
 */
export function BarcodeFormModal({
  articleId,
  fixedArticle,
  raw,
  mode = 'manual',
  defaultSupplierId,
  onClose,
  onSaved,
}: {
  articleId: string;
  /** Rendered instead of an article picker when the context already knows it. */
  fixedArticle?: string;
  /** The untouched scan, kept for the audit trail on the teach path. */
  raw?: string;
  mode?: 'manual' | 'teach';
  defaultSupplierId?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const { options: supplierOptions } = useSuppliers();
  const { options: unitOptions } = useUnits();
  const [conflict, setConflict] = useState<BarcodeConflictParams | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BarcodeFormValues>({
    defaultValues: {
      articleId,
      barcode: raw ?? '',
      kind: '',
      supplierId: defaultSupplierId ?? '',
      unitId: '',
      qtyPerScan: '1',
      note: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: BarcodeFormValues) => {
      const payload = {
        articleId,
        barcode: values.barcode.trim(),
        ...(values.kind ? { kind: values.kind } : {}),
        supplierId: values.supplierId || null,
        unitId: values.unitId || null,
        qtyPerScan: values.qtyPerScan.trim() || '1',
        note: values.note.trim() || null,
      } as BarcodeCreate;
      return mode === 'teach'
        ? teachBarcode({ ...payload, raw: raw ?? values.barcode.trim() })
        : createBarcode(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'barcodes'] });
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'article'] });
      toastSuccess(mode === 'teach' ? t('masterData:barcodes.teach.success') : t('masterData:barcodes.created'));
      onSaved?.();
      onClose();
    },
    onError: (error: unknown) => {
      const taken = barcodeConflictOf(error);
      if (taken) {
        setConflict(taken);
        return;
      }
      toastError(error);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'teach' ? t('masterData:barcodes.teach.title') : t('masterData:barcodes.new')}
      subtitle={mode === 'teach' ? t('masterData:barcodes.teach.subtitle') : undefined}
      width={620}
    >
      <form
        onSubmit={(event) => {
          setConflict(null);
          void handleSubmit((values) => {
            mutation.mutate(values);
          })(event);
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        {conflict ? (
          <BarcodeConflictNotice
            conflict={conflict}
            onDismiss={() => {
              setConflict(null);
            }}
          />
        ) : null}

        {fixedArticle ? (
          <div>
            <p className="mb-1 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
              {t('masterData:barcodes.fields.article')}
            </p>
            <p className="text-sm text-ink">{fixedArticle}</p>
          </div>
        ) : null}

        <TextField
          label={t('masterData:barcodes.fields.barcode')}
          hint={t('masterData:barcodes.hints.barcode')}
          autoFocus
          error={errors.barcode ? t('common:common.required') : undefined}
          {...register('barcode', { required: true })}
        />

        <SelectField
          label={t('masterData:barcodes.fields.kind')}
          hint={t('masterData:barcodes.hints.kind')}
          options={[
            { value: '', label: t('common:common.unknown') },
            { value: 'gtin', label: t('masterData:barcodes.kinds.gtin') },
            { value: 'raw', label: t('masterData:barcodes.kinds.raw') },
          ]}
          {...register('kind')}
        />

        <SelectField
          label={t('masterData:barcodes.fields.supplier')}
          hint={t('masterData:barcodes.hints.supplier')}
          options={[{ value: '', label: t('masterData:barcodes.scan.anySupplier') }, ...supplierOptions]}
          {...register('supplierId')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={t('masterData:barcodes.fields.unit')}
            options={[{ value: '', label: '—' }, ...unitOptions]}
            {...register('unitId')}
          />
          <TextField
            label={t('masterData:barcodes.fields.qtyPerScan')}
            inputMode="decimal"
            hint={t('masterData:barcodes.hints.qtyPerScan')}
            {...register('qtyPerScan')}
          />
        </div>

        <TextField
          label={t('masterData:barcodes.fields.note')}
          hint={t('masterData:barcodes.hints.note')}
          {...register('note')}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            {mode === 'teach' ? t('masterData:barcodes.teach.submit') : t('common:actions.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
